import type { ReactElement } from 'react';
import { useState } from 'react';
import { FeatureFlag, Route } from 'config';

import useCurrentUser from 'hooks/useCurrentUser';
import useAddresses from 'hooks/useAddresses';

import { Component as CaretIcon } from 'icons/icon-ui--caret-outline.svg';
import { Component as AccountIcon } from 'icons/icon-circle--account.svg';
import { Component as BellIcon } from 'icons/icon-ui--bell.svg';
import { Component as WarningIcon } from 'icons/icon-circle--warning.svg';

import SubLink from './SubLink';

function Header(): ReactElement {
  const { currentUser } = useCurrentUser();
  const { addresses } = useAddresses();
  const [userBadgeHovered, setUserBadgeHovered] = useState(false);
  const hasValidPrimaryAddress = addresses[0]?.isValid;

  const hasAccountManagementEnabled = currentUser.hasFeatureEnabled(
    FeatureFlag.ACCOUNT_MANAGEMENT,
  );
  const {
    email,
    isEmpty,
    isAdmin,
    isEmployee,
    isInactive,
    isSuspended,
    hasGiveaways,
    giveawayCount,
    hasFavoritesEnabled,
    hasValidPaymentMethod,
  } = currentUser;

  const loaded = addresses[0] && !isEmpty;

  const hasAlert = loaded && !isInactive && (
    isSuspended || !hasValidPaymentMethod || !hasValidPrimaryAddress
  );

  return (
    <li className="show-on-mobile">
      <div
        className={[
          'c-username',
          hasAlert ? 'has-alert' : '',
        ].join(' ')}
        onPointerLeave={() => setUserBadgeHovered(true)}
      >
        <WarningIcon
          className={[
            'c-badge',
            'c-badge--warning',
            hasAlert && !userBadgeHovered ? '' : 'is-hidden',
          ].join(' ')}
          focusable="false"
        />

        <BellIcon
          className={[
            'c-badge',
            'c-badge--info-big',
            'is-hidden',
          ].join(' ')}
          focusable="false"
        />

        <AccountIcon
          className="c-svg-fill-primary-color"
          width="30"
          focusable="false"
        />
        &nbsp;
        <CaretIcon
          className="c-svg-fill-primary-color"
          width="12"
          focusable="false"
        />
      </div>

      <ul
        className={[
          'c-nav-header__sublist',
          'c-nav-header__sublist--right',
        ].join(' ')}
      >
        {isInactive ? (
          <li>
            <span
              className={[
                'c-nav-header__sublink',
                'c-nav-header__sublink--email',
              ].join(' ')}
            >
              {email}
            </span>
          </li>
        ) : (
          <>
            <SubLink href={Route.PROFILE} data-cy="header-my-account">
              My Account

              {hasAlert && (
                <WarningIcon
                  className="c-badge c-badge--warning"
                  focusable="false"
                />
              )}
            </SubLink>

            {hasFavoritesEnabled && (
              <SubLink
                href={Route.MY_FAVORITES}
                data-cy="header-my-favorites"
              >
                My Favorites
              </SubLink>
            )}

            {!isEmployee && (
              <SubLink
                href="/giveaways"
                data-cy="header-share-a-box"
              >
                Share a Box

                {hasGiveaways && (
                  <span className="c-badge c-badge--info">
                    {giveawayCount}
                  </span>
                )}
              </SubLink>
            )}

            <SubLink
              href="/redeem-a-gift"
              data-cy="header-redeem-gift"
            >
              Redeem Gift or Discount
            </SubLink>

            {isAdmin && (
              <SubLink href="/admin" data-cy="header-admin" reload>
                Admin
              </SubLink>
            )}
            {hasAccountManagementEnabled && (
              <>
                <SubLink
                  href={Route.REFER}
                  data-cy="header-refer"
                >
                  Refer and Earn
                </SubLink>
                <SubLink
                  href="https://purplecarrot.zendesk.com/hc/en-us"
                  data-cy="header-faqs"
                  reload
                >
                  FAQs
                </SubLink>
                <SubLink
                  href={Route.CONTACT_US}
                  data-cy="header-contact-us"
                  reload
                >
                  Contact Us
                </SubLink>
              </>
            )}
          </>
        )}

        <li>
          <form method="post" action="/users/sign_out">
            <input
              type="hidden"
              name="_method"
              value="delete"
              autoComplete="off"
            />

            <input
              className="c-button-link c-nav-header__sublink"
              data-cy="header-logout"
              type="submit"
              value="Log Out"
            />
          </form>
        </li>
      </ul>
    </li>
  );
}

export default Header;
